import { useState } from 'react';
import { api, type UserRow } from '../lib/api';
import { useFetch } from '../lib/useFetch';
import { useAuth } from '../lib/auth';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import type { PageProps } from '../App';

export function Configuracoes(_: PageProps) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const users = useFetch(() => (isAdmin ? api.listUsers() : Promise.resolve({ items: [] as UserRow[] })), [isAdmin]);
  const tipos = useFetch(() => api.listRetencaoTipos(), []);
  const wf = useFetch(() => api.workflows(), []);

  const [criar, setCriar] = useState(false);
  const [editar, setEditar] = useState<UserRow | null>(null);

  return (
    <div className="page mid">
      <h1 style={{ marginBottom: 20 }}>Configurações</h1>
      <div className="grid-2">
        <div className="tbl">
          <div className="card-head" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ flex: 1 }}>Usuários</span>
            {isAdmin && <button className="btn dark xs" onClick={() => setCriar(true)}>+ Novo usuário</button>}
          </div>
          {!isAdmin ? (
            <div className="loading">Apenas administradores gerenciam usuários.</div>
          ) : users.loading ? (
            <div className="loading">Carregando…</div>
          ) : (
            (users.data?.items ?? []).map((u) => (
              <div className="tbl-row" key={u.id} style={{ display: 'flex', gap: 10, padding: '11px 16px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="cell-strong ellipsis">{u.nome}</div>
                  <div className="text-sm muted ellipsis">{u.email}</div>
                </div>
                <span className={`badge ${u.role === 'admin' ? 'aguardando' : 'neutro'}`}>{u.role}</span>
                {!u.ativo && <span className="badge neutro">inativo</span>}
                <button className="btn link text-sm" onClick={() => setEditar(u)}>editar</button>
              </div>
            ))
          )}
        </div>

        <div className="tbl">
          <div className="card-head">Tipos de retenção — vencimento das guias</div>
          {tipos.loading ? (
            <div className="loading">Carregando…</div>
          ) : (
            (tipos.data?.items ?? []).map((t) => (
              <div className="tbl-row" key={t.codigo} style={{ display: 'flex', gap: 10, padding: '11px 16px' }}>
                <span className="tag-tax" style={{ width: 58 }}>{t.codigo}</span>
                <span style={{ flex: 1 }}>{t.nome}</span>
                <span className="text-sm muted">
                  dia {t.dia_vencimento}{t.meses_apos ? ` · +${t.meses_apos} mês(es)` : ''}
                </span>
              </div>
            ))
          )}
        </div>

        <div className="tbl" style={{ gridColumn: '1 / -1' }}>
          <div className="card-head">Workflows automáticos</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))' }}>
            {(wf.data?.items ?? []).map((w) => (
              <div key={w.id} style={{ padding: '14px 16px', borderRight: '1px solid var(--border-soft)', borderTop: '1px solid var(--border-soft)' }}>
                <div className="row" style={{ gap: 7, marginBottom: 4 }}>
                  <span className={`dot ${w.status === 'ok' ? 'ok' : w.status === 'warn' ? 'warn' : 'off'}`} />
                  <span className="cell-strong text-sm">{w.name}</span>
                </div>
                <div className="text-sm muted">{w.desc}{w.runs_today ? ` · ${w.runs_today} hoje` : ''}</div>
              </div>
            ))}
            {(wf.data?.items.length ?? 0) === 0 && !wf.loading && <div className="loading">Sem workflows.</div>}
          </div>
        </div>
      </div>

      {criar && <NovoUsuarioModal onClose={() => setCriar(false)} onDone={() => { setCriar(false); users.refetch(); }} />}
      {editar && <EditarUsuarioModal u={editar} onClose={() => setEditar(null)} onDone={() => { setEditar(null); users.refetch(); }} />}
    </div>
  );
}

function NovoUsuarioModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [nome, setNome] = useState('');
  const [senha, setSenha] = useState('');
  const [role, setRole] = useState<'admin' | 'user'>('user');
  const [busy, setBusy] = useState(false);

  const salvar = async () => {
    if (!email.trim() || !nome.trim() || senha.length < 6) { toast('Preencha e-mail, nome e senha (mín. 6).', 'err'); return; }
    setBusy(true);
    try {
      await api.createUser({ email: email.trim(), nome: nome.trim(), senha, role });
      toast('Usuário criado.');
      onDone();
    } catch (e) {
      const body = (e as { body?: { error?: string } }).body;
      toast(body?.error === 'email_duplicado' ? 'Já existe usuário com esse e-mail.' : 'Falha ao criar usuário.', 'err');
      setBusy(false);
    }
  };

  return (
    <Modal title="Novo usuário" onClose={onClose}
      footer={<>
        <button className="btn ghost" onClick={onClose}>Cancelar</button>
        <button className="btn primary" onClick={salvar} disabled={busy}>Criar</button>
      </>}>
      <div className="field"><label>E-mail</label><input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus /></div>
      <div className="field"><label>Nome</label><input className="input" value={nome} onChange={(e) => setNome(e.target.value)} /></div>
      <div className="field"><label>Senha (mín. 6)</label><input className="input" type="password" value={senha} onChange={(e) => setSenha(e.target.value)} /></div>
      <div className="field" style={{ marginBottom: 0 }}><label>Papel</label>
        <select className="select" value={role} onChange={(e) => setRole(e.target.value as 'admin' | 'user')}>
          <option value="user">Usuário</option><option value="admin">Administrador</option>
        </select>
      </div>
    </Modal>
  );
}

function EditarUsuarioModal({ u, onClose, onDone }: { u: UserRow; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [nome, setNome] = useState(u.nome);
  const [role, setRole] = useState<'admin' | 'user'>(u.role);
  const [ativo, setAtivo] = useState(u.ativo);
  const [senha, setSenha] = useState('');
  const [busy, setBusy] = useState(false);

  const salvar = async () => {
    if (senha && senha.length < 6) { toast('A nova senha precisa de ao menos 6 caracteres.', 'err'); return; }
    setBusy(true);
    try {
      const patch: { nome?: string; role?: 'admin' | 'user'; ativo?: boolean; senha?: string } = {};
      if (nome.trim() && nome !== u.nome) patch.nome = nome.trim();
      if (role !== u.role) patch.role = role;
      if (ativo !== u.ativo) patch.ativo = ativo;
      if (senha) patch.senha = senha;
      if (Object.keys(patch).length === 0) { onClose(); return; }
      await api.patchUser(u.id, patch);
      toast(senha ? 'Usuário atualizado e senha alterada.' : 'Usuário atualizado.');
      onDone();
    } catch { toast('Falha ao atualizar usuário.', 'err'); setBusy(false); }
  };

  return (
    <Modal title={`Editar — ${u.email}`} onClose={onClose}
      footer={<>
        <button className="btn ghost" onClick={onClose}>Cancelar</button>
        <button className="btn primary" onClick={salvar} disabled={busy}>Salvar</button>
      </>}>
      <div className="field"><label>Nome</label><input className="input" value={nome} onChange={(e) => setNome(e.target.value)} /></div>
      <div className="field"><label>Papel</label>
        <select className="select" value={role} onChange={(e) => setRole(e.target.value as 'admin' | 'user')}>
          <option value="user">Usuário</option><option value="admin">Administrador</option>
        </select>
      </div>
      <div className="field">
        <label className="checkline"><input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} /> Usuário ativo</label>
      </div>
      <div className="field" style={{ marginBottom: 0 }}>
        <label>Nova senha</label>
        <input className="input" type="password" placeholder="deixe em branco para manter" value={senha} onChange={(e) => setSenha(e.target.value)} />
      </div>
    </Modal>
  );
}
